<template>
  <div>
    <responsive-drawer-layout>
      <template v-slot:drawer>
        <user-list :key="tileFilter" @showOnlyMyEmployeesEvent="handleShowOnlyMyEmployees" :tile-filter="tileFilter"></user-list>
      </template>
      <template v-slot:content>
        <div class="begleitungen-container">
          <transition name="fade">
            <v-alert v-if="showAlert" class="font-weight-black mx-5 mt-5" type="error" prominent>
              {{ $t('bf.userNotFound') }}
            </v-alert>
          </transition>

          <ratings-dashboard
            v-if="!currentUser"
            class="ratingsDashboard"
            :show-only-my-employees="showOnlyMyEmployees"
            @show="filterRatings"
          ></ratings-dashboard>

          <rating-overview v-if="currentUser" :user="currentUser" :ratings="ratings" :openRatings="openRatings"></rating-overview>

          <div v-if="hasEvaluations && moduleConfig.enableDiscussions" class="text-h6 mt-6 px-4 mx-auto" style="max-width: 800px">
            {{ $t('bf.evaluationsAcronym') }}
          </div>
          <div class="mx-auto px-3" style="max-width: 800px">
            <v-list v-if="completedOfflineEvaluations || openEvaluations.length || completedEvaluations.length">
              <ratings-list type="completedOfflineEvaluations" :ratings="completedOfflineEvaluations"></ratings-list>
              <ratings-list type="openEvaluations" :ratings="openEvaluations" @onSelected="(assessment) => goToAssessment(assessment)"></ratings-list>
              <ratings-list
                type="completedEvaluations"
                :ratings="completedEvaluations"
                @onSelected="(assessment) => goToRatingResults(assessment)"
              ></ratings-list>
            </v-list>
          </div>

          <div v-if="hasAssessments" class="text-h6 mt-6 px-4 mx-auto" style="max-width: 800px">{{ $t('bf.assessments') }}</div>
          <div class="mx-auto px-3" style="max-width: 800px">
            <v-list v-if="completedOfflineAssessments || openAssessments.length || completedAssessments.length">
              <ratings-list type="completedOfflineAssessments" :ratings="completedOfflineAssessments"></ratings-list>
              <ratings-list type="openAssessments" :ratings="openAssessments" @onSelected="(assessment) => goToAssessment(assessment)"></ratings-list>
              <ratings-list
                type="completedAssessments"
                :ratings="completedAssessments"
                @onSelected="(assessment) => goToRatingResults(assessment)"
              ></ratings-list>
            </v-list>
          </div>
        </div>
      </template>
    </responsive-drawer-layout>
  </div>
</template>

<script>
import common from '@/mixins/common';
import ResponsiveDrawerLayout from '@/layouts/ResponsiveDrawerLayout.vue';
import { mapState } from 'vuex';
import UserList from './views/UserList.vue';
import dateUtils from '@/common/dateUtils';
import configMixin from '@/mixins/configMixin';
import index from './index.js';
import RatingsDashboard from './views/RatingsDashboard.vue';
import RatingOverview from '@/modules/begleitfahrt/views/RatingOverview.vue';
import RatingsList from '@/modules/begleitfahrt/views/RatingsList.vue';
import sortFunctions from '@/sorters/sortFunctions';
import dateSorters from '@/sorters/dateSorters';

export default {
  name: 'Begleitfahrt',
  mixins: [common, configMixin],

  components: {
    RatingsDashboard,
    RatingsList,
    RatingOverview,
    ResponsiveDrawerLayout,
    UserList,
  },
  data() {
    return {
      showOnlyMyEmployees: true,
      tileFilter: '',
    };
  },
  computed: {
    ...mapState('begleitfahrt', ['dataLoaded', 'userById', 'rating', 'questionnaireById', 'questionnaireLocalData', 'questionnaireLocalDataOffline']),

    moduleConfig() {
      return this.getModuleConfig(index.moduleType, this.mx_instance);
    },
    showAlert() {
      return this.dataLoaded && this.$route.params.userId && !this.currentUser;
    },
    userId() {
      return Number(this.$route.params.userId);
    },

    currentUser() {
      return this.userById[this.userId];
    },

    /** Completed MAG & Bewertungen */
    ratings() {
      const ratings = this.rating.filter((rating) => rating.userIdFk === this.userId);

      return ratings
        .filter((r) => this.questionnaireById[r.questionnaireIdFk] !== undefined)
        .map((r) => {
          const questionnaire = this.questionnaireById[r.questionnaireIdFk];

          const title = questionnaire.title ? questionnaire.title : '';

          const dateString = r.clientdate ? dateUtils.reformatDateIsoStringAsYYYYMMDDHHmm(r.clientdate) : '';

          return {
            id: r.id,
            title: title,
            dateIsoStr: r.clientdate,
            date_: new Date(r.clientdate),
            date: dateString,
            type: r.isType,
            grade: r.grade,
          };
        });
    },

    /* Offline MAG & Bewertungen */
    offlineData() {
      if (!this.questionnaireLocalDataOffline[this.userId]) return {};

      return Object.values(this.questionnaireLocalDataOffline[this.userId]).map((q) => {
        const baseData = q.baseData || {};
        const date = baseData.date ? new Date(baseData.date) : null;
        const timeFrom = baseData.timeFrom || null;
        const questionnaireId = q.questionnaireId;
        const questionnaire = this.questionnaireById[questionnaireId] || {};

        return {
          ...q,
          title: questionnaire.title || 'Unknown Title',
          date: date ? dateUtils.formatAsLocaleShortDate(date) : null,
          time: timeFrom,
          date_: date ? new Date(`${baseData.date} ${timeFrom}`) : null,
        };
      });
    },
    completedOfflineAssessments() {
      if (!this.offlineData) return [];

      return Object.values(this.offlineData)
        .filter((d) => d.isType === 'questionnaire')
        .sort(dateSorters.sortByDateIsoStrDescending);
    },

    completedOfflineEvaluations() {
      if (!this.offlineData) return [];

      return Object.values(this.offlineData)
        .filter((d) => d.isType === 'evaluation')
        .sort(dateSorters.sortByDateIsoStrDescending);
    },

    /** Bewertungen */
    completedAssessments() {
      return this.ratings.filter((r) => r.type === 'questionnaire').sort(dateSorters.sortByDateIsoStrDescending);
    },
    /** MAG */
    completedEvaluations() {
      return this.ratings.filter((r) => r.type === 'evaluation').sort(dateSorters.sortByDateIsoStrDescending);
    },

    /** Open MAG & Bewertungen */
    openRatings() {
      const ratingsObject = this.questionnaireLocalData[this.userId];
      if (!ratingsObject) return [];
      const ratingKeyArray = Object.keys(ratingsObject);

      let openRatings = ratingKeyArray
        .map((uuid) => {
          const rating = ratingsObject[uuid];
          const questionnaireId = rating.questionnaireId;
          const selectedTopicId = rating.selectedTopicId;
          const questionnaire = this.questionnaireById[Number(questionnaireId)];

          if (!questionnaire) {
            return null;
          }

          const title = questionnaire.title ? questionnaire.title : '';
          // const dateString = dateUtils.formatAsLocaleShortDate(new Date(rating.baseData.date));
          const dateString = dateUtils.formatAsLocaleShortDate(new Date(rating.baseData.date));
          const time = rating.baseData.timeFrom;
          const type = questionnaire.isType;
          return {
            uuid,
            questionnaireId,
            selectedTopicId,
            title,
            date_: new Date(`${rating.baseData.date} ${rating.baseData.timeFrom}`),
            date: dateString,
            time,
            type,
          };
        })
        .filter((item) => item !== null);

      return openRatings;
    },
    /** open MAG */
    openEvaluations() {
      return this.openRatings.filter((r) => r.type === 'evaluation').sort(dateSorters.sortByDateIsoStrDescending);
    },
    /** open Bewertungen */
    openAssessments() {
      return this.openRatings.filter((r) => r.type === 'questionnaire').sort(dateSorters.sortByDateIsoStrDescending);
    },
    hasEvaluations() {
      return this.openEvaluations.length + this.completedEvaluations.length;
    },
    hasAssessments() {
      return this.openAssessments.length + this.completedAssessments.length;
    },
  },
  methods: {
    /**
     * @param {Boolean} showOnlyMyEmployees
     */
    handleShowOnlyMyEmployees(showOnlyMyEmployees) {
      this.showOnlyMyEmployees = showOnlyMyEmployees;
    },
    onSelected(assessment) {
      this.goToAssessment(assessment.questionnaireId, assessment.uuid);
    },
    goToRatingResults(rating) {
      this.$router.push(`/begleitfahrt/${this.mx_instance}/${this.userId}/${rating.id}`);
    },
    goToAssessment(assessment) {
      this.$router.push(
        `/begleitfahrt/${this.mx_instance}/bewertung/${this.userId}/${assessment.questionnaireId}/${assessment.uuid}/${assessment.selectedTopicId}`
      );
    },
    filterRatings(data) {
      this.tileFilter = data;
    },
  },
  mounted() {
    if (!this.currentUser) {
      this.mx_isLeftDrawerOpen = true;
    }
  },
};
</script>

<style scoped>
.ratingsDashboard {
  background-color: #f5f5f5;
  height: 100%;
}

.begleitungen-container {
  max-height: 100%;
  overflow-y: auto;
}
</style>
